import CompanyLookup from '../CompanyLookup';
import { useContext, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Box, Button, Divider, FormControl, FormHelperText, FormLabel,
  IconButton, Input, Modal, ModalDialog, ModalOverflow, Select, Option,
  Sheet, Stack, Typography
} from "@mui/joy";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import { LayoutContext } from "../../context";
import OrderModal from "./OrderModal";
import { APIClientRequestCall, APIClientRequestEmail, APIClientVerifyCode, APIClientUpdateProfile } from "../../api";
import MaskedInput from "../JoyMaskedInput";
import { splitFullName } from "../../shared/nameUtils";
import { needsClientProfile, clientPermissions } from '../../shared/clientSession';
import { isApiError } from "../../shared/apiError";


// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatPhone(raw) {
  const digits = raw.replace(/\D/g, "").slice(0, 11);
  if (!digits) return "";
  let r = "+7";
  if (digits.length > 1) r += " (" + digits.slice(1, 4);
  if (digits.length >= 4) r += ") " + digits.slice(4, 7);
  if (digits.length >= 7) r += "-" + digits.slice(7, 9);
  if (digits.length >= 9) r += "-" + digits.slice(9, 11);
  return r;
}

// ─── Step 1: Phone ────────────────────────────────────────────────────────────
function PhoneStep({ onNext, onQuickOrder, materials = false }) {
  const [method, setMethod] = useState("phone");

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Реф. токен: подставляем из ссылки (localStorage), поле по умолчанию скрыто.
  const [refToken, setRefToken] = useState(
    () => (localStorage.getItem("pending_referral_code") || "").trim()
  );
  const [showRef, setShowRef] = useState(false);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 300); }, []);

  const handleInput = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    if (digits.length <= 11) { setPhone(formatPhone(digits)); setError(""); }
  };

  const handleSubmit = async () => {
    if (method === "email") {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email?.trim())) {
        setError("Введите корректный email");
        return;
      }

      setLoading(true);
      const res = await APIClientRequestEmail(email?.trim(), refToken.trim());
      setLoading(false);

      if (res?.status === "ok") {
        onNext(email?.trim(), "email");
      } else {
        // detail уже содержит нормализованный текст: сетевой сбой,
        // ограничение по частоте или сообщение сервера
        setError(res?.message || res?.detail || "Не удалось отправить код на почту");
      }
      // Сервер вернул 429 и сказал, сколько ждать — показываем это явно,
      // иначе человек жмёт кнопку снова и снова
      if (res?.code === 'rate_limit' && res?.retry_after) {
        setError(`${res.message} Повторить можно через ${res.retry_after} с.`);
      }
      return;
    }

    const digits = phone.replace(/\D/g, "");
    if (digits.length < 11) { setError("Введите полный номер телефона"); return; }
    setLoading(true);
    const res = await APIClientRequestCall("+" + digits, refToken.trim());
    setLoading(false);
    if (res?.status === "ok") {
      onNext("+" + digits, "phone");
    } else {
      setError(res?.message || res?.detail || "Не удалось отправить звонок, попробуйте позже");
    }
  };

  return (
    <motion.div key="phone"
      initial={{ opacity: 0, x: -32 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -32 }}
      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
    >
      <Stack spacing={3}>
        <Box>
          <Typography component="h2" id="client-auth-title" level="h3" sx={{ fontWeight: 800, letterSpacing: "-0.5px" }}>{materials ? 'Откройте материалы своего события' : 'Войти в кабинет'}</Typography>
          <Typography level="body-sm" sx={{ mt: 0.5, color: "text.secondary" }}>
            {materials ? 'Войдите по телефону из заявки или по почте своего кабинета. Для нового кабинета потребуется подтвердить контакт и заполнить профиль.' : 'Введите номер — мы перезвоним для подтверждения'}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            fullWidth
            variant={method === "phone" ? "solid" : "soft"}
            onClick={() => setMethod("phone")}
          >
            Телефон
          </Button>
          <Button
            fullWidth
            variant={method === "email" ? "solid" : "soft"}
            onClick={() => setMethod("email")}
          >
            Email
          </Button>
        </Stack>

        {method === "phone" ? (
          <FormControl error={!!error}>
            <FormLabel>Номер телефона</FormLabel>
            <MaskedInput
              mask="+7 (000) 000-00-00"
              placeholder="+7 (000) 000-00-00"
              value={phone}
              onChange={handleInput}
              onKeyDown={e => e.key === "Enter" && handleSubmit()}
              slotProps={{ input: { ref: inputRef } }}
              startDecorator={<PhoneRoundedIcon />}
              size="lg"
              sx={{ fontSize: "1.05rem", fontWeight: 600, letterSpacing: "0.03em" }}
            />
            {error && <FormHelperText>{error}</FormHelperText>}
          </FormControl>
        ) : (
          <FormControl error={!!error}>
            <FormLabel>Email</FormLabel>
            <Input
              placeholder="example@mail.ru"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                setError("");
              }}
              onKeyDown={e => e.key === "Enter" && handleSubmit()}
              slotProps={{ input: { ref: inputRef } }}
              type="email"
              size="lg"
              sx={{ fontSize: "1.05rem", fontWeight: 600 }}
            />
            {error && <FormHelperText>{error}</FormHelperText>}
          </FormControl>
        )}
        {/* Реф. токен — по умолчанию скрыт; значение из ссылки уже подставлено */}
        {!materials && (showRef ? (
          <FormControl>
            <FormLabel>Реф. токен</FormLabel>
            <Input
              placeholder="Код партнёра"
              value={refToken}
              onChange={(e) => setRefToken(e.target.value)}
              size="lg"
              sx={{ fontSize: "1rem", fontWeight: 600, letterSpacing: "0.04em" }}
            />
          </FormControl>
        ) : (
          <Typography level="body-xs" textAlign="center">
            <Typography component="span" level="body-xs" color="primary" fontWeight={700}
              sx={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={() => setShowRef(true)}
            >
              Ввести реф. токен
            </Typography>
          </Typography>
        ))}

        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Button size="lg" onClick={handleSubmit} loading={loading} sx={{ fontWeight: 700, width: "100%" }}>
            {method === "email" ? "Получить код на почту" : "Получить звонок"}
          </Button>
        </motion.div>
        {!materials && <Divider />}
        {!materials && <Typography level="body-xs" textAlign="center" sx={{ color: "text.tertiary" }}>
          Ещё нет заявки?{" "}
          <Typography component="span" level="body-xs" color="primary" fontWeight={700}
            sx={{ cursor: "pointer", textDecoration: "underline" }} onClick={onQuickOrder}
          >
            Оформить быстро →
          </Typography>
        </Typography>}
        <Typography level="body-xs" textAlign="center" sx={{ color: "text.tertiary" }}>
          Нажимая кнопку, вы соглашаетесь с обработкой персональных данных
        </Typography>
      </Stack>
    </motion.div>
  );
}

// ─── Step 2: Code ─────────────────────────────────────────────────────────────
function CodeStep({ contact, method, onSuccess, onBack }) {
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const [calling, setCalling] = useState(true);
  const [loading, setLoading] = useState(false);
  const refs = [useRef(), useRef(), useRef(), useRef()];
  const verifying = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => { setCalling(false); refs[0].current?.focus(); }, 1800);
    return () => clearTimeout(t);
  }, []);

  const verify = async (code) => {
    if (verifying.current || !/^\d{4}$/.test(code)) return;
    verifying.current = true;
    setLoading(true);
    const res = await APIClientVerifyCode(contact, code, method);
    setLoading(false);
    verifying.current = false;
    if (res?.token && res?.user?.id) {
      localStorage.setItem("client_token", res.token);
      onSuccess(
        res.token,
        res.user,
        needsClientProfile(res.user),
        res.permissions ?? clientPermissions(res.user),
        res.is_child_account ?? Boolean(res.user?.is_child_account),
      );
    } else {
      const text = res?.message || res?.detail || "Неверные цифры. Попробуйте снова.";
      setError(text);

      // Код стираем только если он действительно неверный.
      // При сетевом сбое или ошибке сервера цифры сохраняем —
      // человеку не за что винить себя и незачем набирать заново.
      const keepDigits = ['network', 'timeout', 'server_error'].includes(res?.code);
      if (!keepDigits) {
        setDigits(["", "", "", ""]);
        setTimeout(() => refs[0].current?.focus(), 100);
      }
    }
  };

  const handleDigit = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...digits]; next[i] = val;
    setDigits(next); setError("");
    if (val && i < 3) refs[i + 1].current?.focus();
    if (val && i === 3) setTimeout(() => verify([...next.slice(0, 3), val].join("")), 120);
  };

  const handlePaste = (i, e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4 - i);
    if (!pasted) return;
    const next = [...digits];
    for (let j = 0; j < pasted.length; j++) { if (i + j < 4) next[i + j] = pasted[j]; }
    setDigits(next);
    refs[Math.min(i + pasted.length - 1, 3)].current?.focus();
    if (next.every(d => d !== "")) setTimeout(() => verify(next.join("")), 120);
  };

  return (
    <motion.div key="code"
      initial={{ opacity: 0, x: 32 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 32 }}
      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
    >
      <Stack spacing={3}>
        <Box>
          <Button variant="plain" color="neutral" size="sm"
            startDecorator={<ArrowBackRoundedIcon />} onClick={onBack} sx={{ mb: 1, px: 0 }}
          >
            {method === 'email' ? 'Изменить почту' : 'Изменить номер'}
          </Button>
          <Typography level="h3" sx={{ fontWeight: 800, letterSpacing: "-0.5px" }}>Подтвердите вход</Typography>
          { method == "phone" ?
            <Box sx={{ mt: 1 }}>
              <Typography level="body-sm" sx={{ mt: 0.5, color: "text.secondary" }}>
                Звоним на <strong>{contact}</strong>.<br />
                Введите <strong>последние 4 цифры</strong> входящего номера
              </Typography>
              <motion.div animate={calling ? { opacity: [0.5, 1, 0.5] } : {}} transition={{ repeat: Infinity, duration: 1.4 }}>
                <Sheet variant="soft" color={calling ? "success" : "primary"}
                  sx={{ borderRadius: "xl", p: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}
                >
                  {calling ? <HourglassEmptyRoundedIcon sx={{ fontSize: 18 }} /> : <PhoneRoundedIcon sx={{ fontSize: 18 }} />}
                  <Typography level="body-sm" fontWeight={600}>
                    {calling ? "Выполняем звонок..." : "Введите 4 цифры с экрана телефона"}
                  </Typography>
                </Sheet>
              </motion.div>
            </Box>
          :
            <Typography level="body-sm" sx={{ mt: 0.5, color: "text.secondary" }}>
              Отправили код на <strong>{contact}</strong>.<br />
              Введите <strong>код из 4тырех цифр</strong> входящего сообщения
            </Typography>
          }
        </Box>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1.5} justifyContent="center">
            {digits.map((d, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06, duration: 0.3 }}
              >
                <Box component="input"
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  ref={refs[i]} value={d}
                  onChange={e => handleDigit(i, e.target.value)}
                  onKeyDown={e => {
                    if (e.key !== "Backspace") return;

                    e.preventDefault();

                    const next = [...digits];

                    if (next[i]) {
                      next[i] = "";
                      setDigits(next);
                      if (i > 0) refs[i - 1].current?.focus();
                      return;
                    }

                    if (i > 0) {
                      next[i - 1] = "";
                      setDigits(next);
                      refs[i - 1].current?.focus();
                    }
                  }}
                  onPaste={e => handlePaste(i, e)}
                  disabled={loading}
                  sx={{
                    width: 56, height: 64, textAlign: "center",
                    fontSize: "1.6rem", fontWeight: 800, border: "2px solid",
                    borderColor: d ? "primary.400" : "neutral.300", borderRadius: "14px",
                    outline: "none", background: "transparent", transition: "border-color 0.2s",
                    "&:focus": { borderColor: "primary.500" },
                    fontFamily: "inherit", caretColor: "transparent", opacity: loading ? 0.5 : 1,
                  }}
                />
              </motion.div>
            ))}
          </Stack>
          <AnimatePresence>
            {error && (
              <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <Typography level="body-sm" color="danger" textAlign="center">{error}</Typography>
              </motion.div>
            )}
          </AnimatePresence>
        </Stack>
      </Stack>
    </motion.div>
  );
}

const CLIENT_CATEGORIES = [
  { value: "network_retail",      label: "Сетевой ритейл" },
  { value: "franchisee",          label: "Франчайзи" },
  { value: "management_company",  label: "Управляющая компания" },
  { value: "designer_project",    label: "Проектировщик" },
  { value: "interior_designer",   label: "Дизайнер интерьера" },
  { value: "design_studio",       label: "Студия дизайна интерьера" },
  { value: "individual_person",   label: "Частное лицо" },
];

// ─── Step 3: Profile (для новых юзеров) ──────────────────────────────────────
function ProfileStep({ onDone, initialUser, token, materials = false }) {
  // Если бэк прислал отдельные поля — берём их; иначе парсим full_name
  const initialSplit = splitFullName(initialUser?.full_name);
  const [form, setForm] = useState({
    last_name:  initialUser?.last_name  ?? initialSplit.last_name,
    name:       initialUser?.name       ?? initialSplit.name,
    patronymic: initialUser?.patronymic ?? initialSplit.patronymic,
    phone: initialUser?.phone || "",
    email: initialUser?.email || "",
    client_type: "individual",
    company_name: "",
    inn: "",
    kpp: "", ogrn: "", legal_address: "", director_name: "", director_position: "",
    client_category: "",
  });
  const [errors, setErrors] = useState({});  // объект ошибок по полям
  const [globalError, setGlobalError] = useState(""); // строка для общей ошибки
  const [loading, setLoading] = useState(false);

  const set = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    setErrors(p => ({ ...p, [k]: "" }));
    setGlobalError("");
  };

  const handleProfileSubmit = async () => {
    // мини-валидация
    const e = {};
    if (!form.phone.replace(/\D/g, "")?.trim()) e.phone = "Укажите телефон";
    if (!form.email?.trim()) e.email = "Укажите почту";

    if (!form.last_name?.trim()) e.last_name = "Укажите фамилию";
    if (!form.name?.trim())      e.name      = "Укажите имя";
    // Отчество — необязательное

    if (!form.client_type)      e.client_type = "Выберите тип клиента";
    if (!form.client_category) e.client_category = "Выберите категорию";
    if (form.client_type === "legal") {
      if (!form.company_name?.trim()) e.company_name = "Укажите название компании";
      if (!form.inn?.trim())          e.inn = "Укажите ИНН";
    }
    if (Object.keys(e).length) { setErrors(e); return; }

    setLoading(true);
    const payload = {
      last_name:  form.last_name?.trim(),
      name:       form.name?.trim(),
      patronymic: form.patronymic?.trim(),
      phone: form.phone?.trim(),
      email: form.email?.trim(),
      client_type: form.client_type,
      company_name: form.client_type === "legal" ? form.company_name?.trim() : null,
      inn:          form.client_type === "legal" ? form.inn?.trim() : null,
      kpp: form.client_type === 'legal' ? form.kpp?.trim() || null : null,
      ogrn: form.client_type === 'legal' ? form.ogrn?.trim() || null : null,
      legal_address: form.client_type === 'legal' ? form.legal_address?.trim() || null : null,
      director_name: form.client_type === 'legal' ? form.director_name?.trim() || null : null,
      director_position: form.client_type === 'legal' ? form.director_position?.trim() || null : null,
      client_category: form.client_category,
    };
    const res = await APIClientUpdateProfile(payload);
    setLoading(false);

    if (res?.id) {
      onDone(res); // отдаём наверх свежий профиль с сервера
      return;
    }

    // Раньше объект ошибок сериализатора выводился через JSON.stringify —
    // клиент видел в интерфейсе {"phone":["Обязательное поле"]}.
    setGlobalError(res?.message || res?.detail || "Не удалось сохранить профиль");

    // Ошибки по полям подсвечиваем на конкретных инпутах
    if (isApiError(res) && res.fields && Object.keys(res.fields).length) {
      const map = {
        last_name: "last_name", name: "name", patronymic: "patronymic",
        phone: "phone", email: "email", company_name: "company_name",
        inn: "inn", client_category: "client_category", client_type: "client_type",
      };
      const nextErrors = {};
      Object.entries(res.fields).forEach(([key, text]) => {
        const uiKey = map[key];
        if (uiKey) nextErrors[uiKey] = text;
      });
      if (Object.keys(nextErrors).length) {
        setErrors(prev => ({ ...prev, ...nextErrors }));
      }
    }
  };

  return (
    <motion.div key="profile"
      initial={{ opacity: 0, x: 32 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 32 }}
      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
    >
      <Stack spacing={3}>
        <Box>
          <Typography level="h3" sx={{ fontWeight: 800, letterSpacing: "-0.5px" }}>Добро пожаловать! 👋</Typography>
          <Typography level="body-sm" sx={{ mt: 0.5, color: "text.secondary" }}>
            Заполните данные — это займёт 10 секунд
          </Typography>
        </Box>

        {/* Тип клиента */}
        <Box>
          <Typography level="body-xs" fontWeight={700} sx={{ mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Тип клиента
          </Typography>
          <Sheet variant="soft" color="neutral" sx={{ borderRadius: "12px", p: 0.5, display: "flex", gap: 0.5 }}>
            {[
              { key: "individual", label: "Физ. лицо" },
              { key: "legal",      label: "Юр. лицо" },
            ].map(t => (
              <Button
                key={t.key}
                fullWidth size="sm"
                variant={form.client_type === t.key ? "solid" : "plain"}
                color={form.client_type === t.key ? "primary" : "neutral"}
                onClick={() => set("client_type", t.key)}
                sx={{
                  fontWeight: form.client_type === t.key ? 700 : 500,
                  borderRadius: "10px",
                  ...(form.client_type === t.key && {
                    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                  }),
                }}
              >
                {t.label}
              </Button>
            ))}
          </Sheet>
        </Box>

        {/* Категория клиента */}
        <FormControl error={!!errors.client_category}>
          <FormLabel>
            Кем вы являетесь <Typography component="span" color="danger">*</Typography>
          </FormLabel>
          <Select
            placeholder="Выберите категорию"
            value={form.client_category}
            onChange={(_, val) => set("client_category", val)}
            size="lg"
          >
            {CLIENT_CATEGORIES.map(c => (
              <Option key={c.value} value={c.value}>{c.label}</Option>
            ))}
          </Select>
          {errors.client_category && <FormHelperText>{errors.client_category}</FormHelperText>}
        </FormControl>

        <FormControl error={!!errors.last_name}>
          <FormLabel>Фамилия <Typography component="span" color="danger">*</Typography></FormLabel>
          <Input
            value={form.last_name}
            onChange={e => set("last_name", e.target.value)}
            placeholder="Иванов" size="lg" autoFocus
          />
          {errors.last_name && <FormHelperText>{errors.last_name}</FormHelperText>}
        </FormControl>

        <FormControl error={!!errors.name}>
          <FormLabel>Имя <Typography component="span" color="danger">*</Typography></FormLabel>
          <Input
            value={form.name}
            onChange={e => set("name", e.target.value)}
            placeholder="Алексей" size="lg"
          />
          {errors.name && <FormHelperText>{errors.name}</FormHelperText>}
        </FormControl>

        <FormControl error={!!errors.patronymic}>
          <FormLabel>Отчество <Typography component="span" sx={{ color: "text.tertiary", fontWeight: 400 }}>(необязательно)</Typography></FormLabel>
          <Input
            value={form.patronymic}
            onChange={e => set("patronymic", e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleProfileSubmit()}
            placeholder="Сергеевич" size="lg"
          />
          {errors.patronymic && <FormHelperText>{errors.patronymic}</FormHelperText>}
        </FormControl>

        {initialUser?.email && !initialUser?.phone ? (
          <FormControl error={!!errors.phone}>
            <FormLabel>Номер телефона <Typography component="span" color="danger">*</Typography></FormLabel>
            <MaskedInput
              mask="+7 (000) 000-00-00"
              placeholder="+7 (000) 000-00-00"
              value={form.phone}
              onChange={e => set("phone", e.target.value)}
              size="lg"
            />
            {errors.phone && <FormHelperText>{errors.phone}</FormHelperText>}
          </FormControl>
        ) : (
          <FormControl error={!!errors.email}>
            <FormLabel>Email <Typography component="span" color="danger">*</Typography></FormLabel>
            <Input
              value={form.email}
              onChange={e => set("email", e.target.value)}
              placeholder="example@mail.ru"
              type="email"
              size="lg"
            />
            {errors.email && <FormHelperText>{errors.email}</FormHelperText>}
          </FormControl>
        )}

        {form.client_type === "legal" && (
          <>
            <FormControl error={!!errors.company_name}>
              <FormLabel>Название компании <Typography component="span" color="danger">*</Typography></FormLabel>
              <Input
                value={form.company_name}
                onChange={e => set("company_name", e.target.value)}
                placeholder="ООО «Ромашка»" size="lg"
              />
              {errors.company_name && <FormHelperText>{errors.company_name}</FormHelperText>}
            </FormControl>
            <FormControl error={!!errors.inn}>
              <FormLabel>ИНН <Typography component="span" color="danger">*</Typography></FormLabel>
              <Input
                value={form.inn}
                onChange={e => set("inn", e.target.value)}
                placeholder="7701234567" size="lg"
              />
              {errors.inn && <FormHelperText>{errors.inn}</FormHelperText>}
            </FormControl>
            <CompanyLookup form={form} setForm={setForm} token={token} ownerKey={`registration:${initialUser?.id || ''}`} disabled={loading} />
            {Object.entries({ kpp: 'КПП', ogrn: 'ОГРН', legal_address: 'Юридический адрес', director_name: 'Руководитель (ФИО)', director_position: 'Должность руководителя' }).map(([key, label]) => <FormControl key={key}>
              <FormLabel>{label}</FormLabel><Input value={form[key] || ''} onChange={e => set(key, e.target.value)} />
            </FormControl>)}
          </>
        )}

        {globalError && (
          <Typography level="body-sm" color="danger">{globalError}</Typography>
        )}

        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Button size="lg" onClick={handleProfileSubmit} loading={loading} sx={{ fontWeight: 700, width: "100%" }}>
            {materials ? 'Сохранить и открыть материалы' : 'Войти в кабинет →'}
          </Button>
        </motion.div>
      </Stack>
    </motion.div>
  );
}

// ─── Particles ────────────────────────────────────────────────────────────────
function ModalParticles() {
  return (
    <>
      {[
        { top: "12%", left: "8%", size: 6, delay: 0 },
        { top: "70%", left: "5%", size: 4, delay: 0.4 },
        { top: "30%", right: "10%", size: 5, delay: 0.8 },
        { top: "80%", right: "8%", size: 3, delay: 0.2 },
      ].map((p, i) => (
        <motion.div key={i} style={{
          position: "absolute", top: p.top, left: p.left, right: p.right,
          width: p.size, height: p.size, borderRadius: "50%",
          background: "var(--joy-palette-primary-200)", opacity: 0.5, pointerEvents: "none",
        }}
          animate={{ y: [0, -8, 0], opacity: [0.3, 0.6, 0.3] }}
          transition={{ repeat: Infinity, duration: 3 + i * 0.5, delay: p.delay, ease: "easeInOut" }}
        />
      ))}
    </>
  );
}

// ─── Main AuthModal ───────────────────────────────────────────────────────────
export default function AuthModal({ open, onClose, required = false, purpose }) {
  const materials = purpose === 'materials';
  const [step, setStep] = useState("phone"); // "phone" | "code" | "profile"
  const [pendingContact, setPendingContact] = useState("");
  const [pendingMethod, setPendingMethod] = useState("phone");
  const [pendingAuth, setPendingAuth]   = useState(null); // { token, user }
  const [showOrder, setShowOrder]     = useState(false);

  const { login, account } = useContext(LayoutContext);

  useEffect(() => {
    const reset = () => { setPendingAuth(null); setStep('phone'); };
    window.addEventListener('client_unauthorized', reset);
    return () => window.removeEventListener('client_unauthorized', reset);
  }, []);

  useEffect(() => {
    if (open && needsClientProfile(account)) {
      setPendingAuth({ token: localStorage.getItem('client_token'), user: account });
      setStep('profile');
    }
  }, [open, account]);



  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => { setStep("phone"); setPendingMethod("phone"); setPendingAuth(null); }, 400);
      return () => clearTimeout(t);
    }
  }, [open]);

  const handleCodeSuccess = (token, userData, isNew, perms, isChild) => {
    // Дочерний аккаунт — никогда не показываем ProfileStep
    if (isNew && !isChild) {
      setPendingAuth({ token, user: userData });
      setStep("profile");
      login(token, userData, true, perms, isChild);
    } else {
      login(token, userData, false, perms, isChild);
    }
  };

  // profileData = { full_name, email } — то что пользователь только что ввёл
  const handleProfileDone = (freshProfile) => {
    if (pendingAuth) {
      login(pendingAuth.token, freshProfile, false, clientPermissions(freshProfile), Boolean(freshProfile.is_child_account));
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <Modal
            open={open}
            onClose={(_, reason) => {
                if (required || step === "profile") return;
                onClose?.();
            }}
            sx={{ backdropFilter: "blur(8px)" }}
            slotProps={{
              backdrop: {
                component: motion.div,
                initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 },
                transition: { duration: 0.3 },
              }
            }}
          >
            <ModalOverflow sx={{ py: {xs: 2, sm: 4} }}>
              <ModalDialog
                layout="center"
                aria-label={materials ? 'Вход для просмотра материалов события' : 'Вход в личный кабинет'}
                sx={{
                  maxWidth: materials ? 480 : 440, width: 'calc(100% - 32px)', borderRadius: "28px", p: {xs: 2.5, sm: 4},
                  maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto',
                  boxShadow: "0 32px 96px rgba(0,0,0,0.22), 0 0 0 1px rgba(99,102,241,0.08)",
                  border: "1.5px solid",
                  borderColor: "var(--joy-palette-neutral-100)", position: "relative",
                }}
                component={motion.div}
                initial={{ opacity: 0, scale: 0.94, y: 24 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.94, y: 24 }}
                transition={{ duration: 0.35, ease: [0.34, 1.56, 0.64, 1] }}
              >
                <ModalParticles />

                {!required && step !== "profile" && (
                  <IconButton variant="soft" color="neutral" size="sm" onClick={onClose}
                    sx={{ position: "absolute", top: 14, right: 14, borderRadius: "50%", zIndex: 10 }}
                  >
                    <CloseRoundedIcon />
                  </IconButton>
                )}

                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <motion.div whileHover={{ rotate: [-5, 5, -3, 0] }} transition={{ duration: 0.4 }} style={{ display: "flex" }}>
                    <Sheet component="img" src="/logo.png" alt="Planium" sx={{ width: 38, height: 38 }} />
                  </motion.div>
                  <Box>
                    <Typography level="title-sm" fontWeight={800} sx={{ letterSpacing: "-0.3px" }}>PLANIUM</Typography>
                    <Typography level="body-xs" color="neutral" sx={{ mt: -0.2 }}>Ваш персональный замер!</Typography>
                  </Box>
                </Stack>

                {materials && <Sheet variant="soft" color="primary" sx={{p: 1.25, borderRadius: '12px', display: 'flex', gap: 1, alignItems: 'flex-start'}}>
                  <LockRoundedIcon sx={{fontSize: 19, mt: 0.2}}/>
                  <Typography level="body-sm">Материалы доступны заказчику после входа. Проверим связь кабинета с заявкой и откроем ваши файлы.</Typography>
                </Sheet>}

                <Stack direction="row" spacing={0.75} sx={{ mb: 3 }}>
                  {["phone", "code", "profile"].map((s) => (
                    <motion.div key={s}
                      style={{ height: 3, borderRadius: 2, background: "var(--joy-palette-neutral-200)" }}
                      animate={{
                        flex: step === s ? 2 : 1,
                        background: step === s ? "var(--joy-palette-primary-500)" : "var(--joy-palette-neutral-200)",
                      }}
                      transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                    />
                  ))}
                </Stack>

                <AnimatePresence mode="wait">
                  {step === "phone" && (
                    <PhoneStep key="phone"
                      materials={materials}
                      onNext={(contact, method) => {
                        setPendingContact(contact);
                        setPendingMethod(method);
                        setStep("code");
                      }}
                      onQuickOrder={() => { onClose?.(); setShowOrder(true); }}
                    />
                  )}
                  {step === "code" && (
                    <CodeStep
                      contact={pendingContact}
                      method={pendingMethod}
                      onSuccess={handleCodeSuccess}
                      onBack={() => setStep("phone")}
                    />
                  )}
                  {step === "profile" && (
                    <ProfileStep key="profile" materials={materials} token={pendingAuth?.token} initialUser={pendingAuth?.user} onDone={handleProfileDone} />
                  )}
                </AnimatePresence>
              </ModalDialog>
            </ModalOverflow>
          </Modal>
        )}
      </AnimatePresence>

      <OrderModal open={showOrder} onClose={() => setShowOrder(false)} />
    </>
  );
}
